/**
 * @file Camera perspective inclinee (~50 deg) cadree en largeur de monde fixe (portrait, zoom lisse du hub), suivi lisse du chef, tremblement d'ecran.
 */
import { MathUtils, PerspectiveCamera, Vector3 } from 'three';
import { RENDER } from '@config/index';

/** Tremblement d'ecran (explosions, crits, boss) : coupe pour le moment. */
const SCREEN_SHAKE = false;

/** Vitesse de convergence du zoom (1/s) : transition douce, sans saut. */
const ZOOM_SMOOTHING = 7;

export class CameraRig {
  readonly camera: PerspectiveCamera;
  private readonly target = new Vector3();
  private readonly desired = new Vector3();
  private shakeAmp = 0;
  private shakeT = 0;
  /** Largeur de monde cadree du mode (avant zoom). */
  fitWidth = RENDER.camera.fitWidth;
  /** Zoom lisse applique au cadrage : largeur effective = fitWidth / zoom (1 = defaut ; hub : 0.5..2.2). */
  private zoomNow = 1;
  follow = false;
  /** Lacet courant (deg) : fixe par le mode (ModeRules.cameraYawDeg), sinon RENDER.camera.yawDeg. */
  yawDeg = RENDER.camera.yawDeg;

  constructor(aspect: number) {
    // plan lointain genereux : le zoom arriere du hub (x0.5) double la distance de la camera
    this.camera = new PerspectiveCamera(RENDER.camera.fovDeg, aspect, 0.5, 600);
  }

  /**
   * Distance pour que `fitWidth` unites tiennent dans la largeur a la profondeur du point vise :
   * largeurVisible = 2 * d * tan(fovH/2), fovH = 2 * atan(tan(fovV/2) * aspect).
   */
  private distanceFor(aspect: number): number {
    const fovV = MathUtils.degToRad(this.camera.fov);
    const tanH = Math.tan(fovV / 2) * aspect;
    return this.fitWidth / this.zoomNow / 2 / tanH;
  }

  /** Zoom courant (lisse). */
  get zoom(): number {
    return this.zoomNow;
  }

  /**
   * Rapproche le zoom affiche de `target` (exponentielle, `dt` en s) ; `snap` = saut immediat (entree de mode).
   * A appeler avant de lire `viewDistance` pour que le brouillard suive le cadrage.
   */
  setZoom(target: number, dt: number, snap = false): void {
    const t = Math.min(4, Math.max(0.2, target));
    if (snap) this.zoomNow = t;
    else this.zoomNow += (t - this.zoomNow) * (1 - Math.exp(-ZOOM_SMOOTHING * dt));
    if (Math.abs(this.zoomNow - t) < 1e-4) this.zoomNow = t;
  }

  /** Distance camera -> point cadre (brouillard du sol, relatif au plan de jeu). */
  get viewDistance(): number {
    return this.distanceFor(this.camera.aspect);
  }

  resize(aspect: number): void {
    this.camera.aspect = aspect;
    this.camera.updateProjectionMatrix();
  }

  /** Point vise au sol (Three : x, 0, z). En arene fixe on vise (0,0) ; en suivi on vise le chef. */
  setTarget(x: number, z: number, snap = false): void {
    this.desired.set(x, 0, z);
    if (snap) this.target.copy(this.desired);
  }

  /** Tremblement d'ecran : desactive pour le moment (SCREEN_SHAKE = false), l'API reste en place pour les FX. */
  shake(amplitude: number): void {
    if (!SCREEN_SHAKE) return;
    this.shakeAmp = Math.min(0.6, this.shakeAmp + amplitude);
  }

  /** Point vise actuel (Three x, z) : le sol et les falaises du chemin suivent la camera. */
  get focusX(): number {
    return this.target.x;
  }
  get focusZ(): number {
    return this.target.z;
  }

  /** Ecart entre le point vise et la cible desiree (teleportation => recadrage instantane). */
  get lag(): number {
    return this.target.distanceTo(this.desired);
  }

  update(dt: number): void {
    const k = 1 - Math.exp(-RENDER.camera.followLerp * dt);
    this.target.lerp(this.desired, k);
    const pitch = MathUtils.degToRad(RENDER.camera.pitchDeg);
    const yaw = MathUtils.degToRad(this.yawDeg);
    const d = this.distanceFor(this.camera.aspect);
    this.shakeT += dt * 40;
    this.shakeAmp *= Math.exp(-8 * dt);
    const sx = Math.sin(this.shakeT * 1.3) * this.shakeAmp;
    const sz = Math.cos(this.shakeT * 1.7) * this.shakeAmp;
    this.camera.position.set(
      this.target.x + Math.sin(yaw) * Math.cos(pitch) * d + sx,
      this.target.y + Math.sin(pitch) * d,
      this.target.z + Math.cos(yaw) * Math.cos(pitch) * d + sz,
    );
    this.camera.lookAt(this.target.x + sx, 0, this.target.z + sz);
  }
}
